// Account form — FE-040.
//
// Pure presentation: state + submit handler live in the parent
// (``AccountFormPage``) so create-mode and edit-mode share the same
// shape without duplicating effects. Sections + fields are
// announced through native ``<fieldset>`` / ``<legend>`` so
// keyboard / screen-reader navigation reads cleanly.

import type { FormEvent } from "react";
import { Link } from "react-router-dom";

import { AccountTagsPicker } from "./AccountTagsPicker";
import { IndustryClassification } from "./IndustryClassification";
import {
  type AccountFormErrors, formatEin,
} from "./accountFormValidation";
import { useBrokerLocationsForAccountsQuery } from "./hooks";
import type {
  AccountFormMode, AccountFormValues, AccountRiskClass,
} from "./types";


export interface AccountFormProps {
  mode: AccountFormMode;
  values: AccountFormValues;
  errors: AccountFormErrors;
  onChange: <K extends keyof AccountFormValues>(
    key: K, value: AccountFormValues[K],
  ) => void;
  onSubmit: () => void;
  isPending: boolean;
  /** Banner copy + ``role="alert"`` when the mutation errors. */
  mutationError: Error | null;
  /** Cancel target — list when creating, detail when editing. */
  cancelHref: string;
}


export function AccountForm({
  mode, values, errors, onChange, onSubmit,
  isPending, mutationError, cancelHref,
}: AccountFormProps) {
  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit();
  }
  return (
    <form
      data-testid="account-form"
      onSubmit={handleSubmit}
      noValidate
      className="flex w-full max-w-3xl flex-col gap-6"
    >
      {mutationError && (
        <div
          data-testid="account-form-mutation-error"
          role="alert"
          className="rounded-md bg-ember-tint px-3 py-2 text-sm text-ember-ink"
        >
          Couldn't save this Account. Refresh and try again, or contact support.
        </div>
      )}

      <Section legend="Basic details">
        <Field
          name="legal_name" label="Legal name" required
          values={values} errors={errors} onChange={onChange}
          placeholder="Acme Health, Inc."
        />
        <Field
          name="dba_name" label="DBA"
          values={values} errors={errors} onChange={onChange}
          placeholder="Optional"
        />
        <EinField
          values={values} errors={errors} onChange={onChange}
        />
        <Field
          name="bor_signed_at" label="BOR signed"
          type="date"
          values={values} errors={errors} onChange={onChange}
        />
      </Section>

      <Section legend="Employee counts">
        <Field
          name="employee_count_total" label="Total employees"
          type="number" min={0}
          values={values} errors={errors} onChange={onChange}
        />
        <Field
          name="employee_count_eligible" label="Eligible employees"
          type="number" min={0}
          values={values} errors={errors} onChange={onChange}
        />
      </Section>

      <SegmentationSection
        values={values}
        errors={errors}
        onChange={onChange}
      />

      <Section legend="Address">
        <Field
          name="primary_address_line1" label="Address line 1"
          values={values} errors={errors} onChange={onChange}
        />
        <Field
          name="primary_address_line2" label="Address line 2"
          values={values} errors={errors} onChange={onChange}
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field
            name="primary_address_city" label="City"
            values={values} errors={errors} onChange={onChange}
          />
          <Field
            name="primary_address_state" label="State"
            values={values} errors={errors} onChange={onChange}
          />
          <Field
            name="primary_address_zip" label="ZIP"
            values={values} errors={errors} onChange={onChange}
          />
        </div>
      </Section>

      <div className="flex items-center gap-2">
        <button
          type="submit"
          data-testid="account-form-submit"
          disabled={isPending}
          className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse transition-fast ease-out hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitLabel(mode, isPending)}
        </button>
        <Link
          to={cancelHref}
          data-testid="account-form-cancel"
          className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary"
        >
          Cancel
        </Link>
      </div>
    </form>
  );
}


function submitLabel(mode: AccountFormMode, isPending: boolean): string {
  if (mode === "create") return isPending ? "Creating…" : "Create Account";
  return isPending ? "Saving…" : "Save changes";
}


function Section({
  legend, children,
}: { legend: string; children: React.ReactNode }) {
  return (
    <fieldset
      data-testid={`account-form-section-${legend.toLowerCase().replace(/\s+/g, "-")}`}
      className="flex flex-col gap-3 rounded-md border border-border-subtle bg-raised p-4"
    >
      <legend className="px-1 text-xs font-medium uppercase tracking-wide text-text-tertiary">
        {legend}
      </legend>
      {children}
    </fieldset>
  );
}


// Keys whose values are plain ``string`` (excluding the array
// ``account_tag_ids`` which has its own picker).
type StringKey = {
  [K in keyof AccountFormValues]:
    AccountFormValues[K] extends string ? K : never
}[keyof AccountFormValues];


interface FieldProps {
  name: StringKey;
  label: string;
  values: AccountFormValues;
  errors: AccountFormErrors;
  onChange: <K extends keyof AccountFormValues>(
    key: K, value: AccountFormValues[K],
  ) => void;
  type?: "text" | "number" | "date";
  required?: boolean;
  placeholder?: string;
  min?: number;
}


function EinField({
  values, errors, onChange,
}: {
  values: AccountFormValues;
  errors: AccountFormErrors;
  onChange: <K extends keyof AccountFormValues>(
    key: K, value: AccountFormValues[K],
  ) => void;
}) {
  const error = errors.ein;
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium text-text-primary">EIN</span>
      <input
        data-testid="account-form-input-ein"
        name="ein"
        type="text"
        // ``inputMode="numeric"`` triggers the digit pad on mobile
        // even though the canonical EIN renders with a dash.
        inputMode="numeric"
        autoComplete="off"
        value={values.ein}
        // Format on every keystroke so the dash appears as the user
        // types — input is ``XX-XXXXXXX`` once they reach 9 digits.
        onChange={(e) => onChange("ein", formatEin(e.target.value))}
        // Re-format on blur so a paste like ``" 12-3456789 "`` or
        // ``"12 34 56 789"`` lands clean.
        onBlur={(e) => onChange("ein", formatEin(e.target.value))}
        placeholder="12-3456789"
        maxLength={10}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={error ? "account-form-error-ein" : undefined}
        className="rounded-md border border-border-subtle bg-raised px-3 py-2 text-text-primary outline-none focus:border-teal focus:shadow-focus"
      />
      {error && (
        <span
          id="account-form-error-ein"
          data-testid="account-form-error-ein"
          className="text-xs text-ember-ink"
        >
          {error}
        </span>
      )}
    </label>
  );
}


function SegmentationSection({
  values, errors, onChange,
}: {
  values: AccountFormValues;
  errors: AccountFormErrors;
  onChange: <K extends keyof AccountFormValues>(
    key: K, value: AccountFormValues[K],
  ) => void;
}) {
  const locationsQuery = useBrokerLocationsForAccountsQuery();
  const locations = (locationsQuery.data?.rows ?? []).filter(
    (r) => r.status === "active",
  );

  return (
    <fieldset
      data-testid="account-form-section-segmentation"
      className="flex flex-col gap-3 rounded-md border border-border-subtle bg-raised p-4"
    >
      <legend className="px-1 text-xs font-medium uppercase tracking-wide text-text-tertiary">
        Segmentation
      </legend>
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium text-text-primary">
          Primary Broker Location
        </span>
        <select
          data-testid="account-form-input-primary_location_id"
          value={values.primary_location_id}
          onChange={(e) =>
            onChange("primary_location_id", e.target.value)
          }
          className="rounded-md border border-border-subtle bg-raised px-3 py-2 text-text-primary outline-none focus:border-teal focus:shadow-focus"
        >
          <option value="">No location selected</option>
          {locations.map((loc) => {
            const cityState = [loc.city, loc.state_code]
              .filter(Boolean).join(", ");
            const label =
              cityState ? `${loc.name} (${cityState})` : loc.name;
            return (
              <option key={loc.location_id} value={loc.location_id}>
                {label}
              </option>
            );
          })}
        </select>
      </label>

      <IndustryClassification
        sector={values.naics_sector}
        code={values.naics_code}
        codeTitle={values.naics_title}
        riskClass={values.risk_class as "" | AccountRiskClass}
        onChange={(next) => {
          if (next.sector !== undefined) {
            onChange("naics_sector", next.sector);
          }
          if (next.code !== undefined) onChange("naics_code", next.code);
          if (next.codeTitle !== undefined) {
            onChange("naics_title", next.codeTitle);
          }
          if (next.riskClass !== undefined) {
            onChange("risk_class", next.riskClass);
          }
        }}
      />
      {errors.naics_sector && (
        <span
          id="account-form-error-naics_sector"
          data-testid="account-form-error-naics_sector"
          className="text-xs text-ember-ink"
        >
          {errors.naics_sector}
        </span>
      )}

      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium text-text-primary">
          Account tags
        </span>
        <AccountTagsPicker
          selectedIds={values.account_tag_ids}
          onChange={(next) => onChange("account_tag_ids", next)}
        />
      </label>
    </fieldset>
  );
}


function Field({
  name, label, values, errors, onChange,
  type = "text", required, placeholder, min,
}: FieldProps) {
  const error = errors[name];
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium text-text-primary">
        {label}
        {required && (
          <span className="ml-0.5 text-ember-ink" aria-hidden="true">
            *
          </span>
        )}
      </span>
      <input
        data-testid={`account-form-input-${name}`}
        name={name}
        type={type}
        value={values[name]}
        onChange={(e) => onChange(name, e.target.value)}
        required={required}
        aria-required={required ? "true" : undefined}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={error ? `account-form-error-${name}` : undefined}
        placeholder={placeholder}
        min={min}
        className="rounded-md border border-border-subtle bg-raised px-3 py-2 text-text-primary outline-none focus:border-teal focus:shadow-focus"
      />
      {error && (
        <span
          id={`account-form-error-${name}`}
          data-testid={`account-form-error-${name}`}
          className="text-xs text-ember-ink"
        >
          {error}
        </span>
      )}
    </label>
  );
}
